import React from 'react';
import { CaretLeft, CaretRight, CheckCircle, Warning, WarningCircle, X } from '@phosphor-icons/react';
import { Banner, Button, Text, Flex } from '@chatsdet/ui';
import { NODE_TYPE_LABEL } from '@chatsdet/types';
import type { FlowValidationResult, NodeValidationError } from './types';

type ValidationBannerProps = {
  validationResult: FlowValidationResult;
  currentErrorIndex: number;
  currentError: NodeValidationError | null;
  onPrev: () => void;
  onNext: () => void;
  onDismiss: () => void;
};

const nodeTypeLabels: Record<string, string> = {
  login: NODE_TYPE_LABEL.login,
  navigate: NODE_TYPE_LABEL.navigate,
  action: NODE_TYPE_LABEL.action,
  assertion: NODE_TYPE_LABEL.assertion,
  condition: NODE_TYPE_LABEL.condition,
  wait: NODE_TYPE_LABEL.wait,
  setup: 'Setup',
  'seed-output': 'Seed Output',
};

export function ValidationBanner({
  validationResult,
  currentErrorIndex,
  currentError,
  onPrev,
  onNext,
  onDismiss,
}: ValidationBannerProps) {
  if (validationResult.isValid && validationResult.errors.length === 0) {
    return (
      <div className="absolute top-16 right-4 z-30">
        <Banner variant="success" className="shadow-lg whitespace-nowrap">
          <Flex align="center" gap="sm">
            <CheckCircle size={18} weight="fill" className="text-emerald-600 dark:text-emerald-400 shrink-0" />
            <Text size="sm" weight="medium">All nodes configured</Text>
          </Flex>
        </Banner>
      </div>
    );
  }

  const allErrors = validationResult.errors;
  const realErrors = allErrors.filter((e) => e.severity !== 'warning');
  const warnings = allErrors.filter((e) => e.severity === 'warning');
  const errorCount = realErrors.length;
  const warningCount = warnings.length;

  const label = currentError
    ? `${nodeTypeLabels[currentError.nodeType] || currentError.nodeType} node: ${currentError.message}`
    : '';

  const isCurrentWarning = currentError?.severity === 'warning';
  const bannerVariant = errorCount > 0 ? 'error' : 'warning';

  // Build summary text
  const parts: string[] = [];
  if (errorCount > 0) parts.push(`${errorCount} ${errorCount === 1 ? 'issue' : 'issues'}`);
  if (warningCount > 0) parts.push(`${warningCount} ${warningCount === 1 ? 'warning' : 'warnings'}`);
  const summaryText = parts.join(', ');

  return (
    <div className="absolute top-16 right-4 z-30 max-w-sm">
      <Banner variant={bannerVariant} className="shadow-lg">
        <Flex direction="column" gap="xs" className="min-w-0 flex-1">
          <Flex align="center" gap="sm">
            {errorCount > 0 ? (
              <WarningCircle size={18} weight="fill" className="text-red-600 dark:text-red-400 shrink-0" />
            ) : (
              <Warning size={18} weight="fill" className="text-amber-600 dark:text-amber-400 shrink-0" />
            )}
            <Text size="sm" weight="medium">
              {summaryText}
            </Text>
            {allErrors.length > 1 && (
              <Flex align="center" gap="xs" className="shrink-0">
                <Button variant="icon" size="sm" iconOnly tooltip="Previous error" onPress={onPrev}>
                  <CaretLeft size={14} weight="bold" />
                </Button>
                <Text size="xs" className="tabular-nums">
                  {currentErrorIndex + 1}/{allErrors.length}
                </Text>
                <Button variant="icon" size="sm" iconOnly tooltip="Next error" onPress={onNext}>
                  <CaretRight size={14} weight="bold" />
                </Button>
              </Flex>
            )}
          </Flex>
          {currentError && (
            <Text size="sm" className={`${isCurrentWarning ? 'text-amber-700 dark:text-amber-400' : 'text-red-700 dark:text-red-400'} break-words`}>
              {label}
            </Text>
          )}
        </Flex>
        <Button variant="icon" size="sm" iconOnly tooltip="Dismiss" onPress={onDismiss} className="shrink-0">
          <X size={14} />
        </Button>
      </Banner>
    </div>
  );
}
